// Canvas-drawn foliage textures (no image files to download or license).
// Leaves are drawn as many small, sharp-edged shapes rather than big soft
// blobs, so the alpha-test cut-out reads as individual leaves up close.

import { DynamicTexture, Scene } from './babylon';
import { ATLAS_SIZE, BARK_SPRITE, LEAF_SPRITES, rng, type Rect } from './vegetationTrees';

const WRAP = 1;

/** Colour `c` at brightness `k`; the brightest (sunlit) leaves warm up towards yellow. */
const rgb = (c: [number, number, number], k: number): string => {
  const warm = Math.max(0, k - 0.95);
  const r = Math.min(255, Math.round(c[0] * k + 70 * warm));
  const g = Math.min(255, Math.round(c[1] * k + 30 * warm));
  const b = Math.min(255, Math.round(c[2] * k - 20 * warm));
  return `rgb(${r},${g},${Math.max(0, b)})`;
};

/** One pointed leaf from (x, y) along `angle`. */
function leaf(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, length: number, width: number): void {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  ctx.setTransform(c, s, -s, c, x, y);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.quadraticCurveTo(length * 0.4, -width, length, 0);
  ctx.quadraticCurveTo(length * 0.4, width, 0, 0);
  ctx.fill();
}

/**
 * A cluster of leaves on radiating twigs around a shadowed core. Lit from
 * above: leaves near the top of the sprite are lighter, the core is dark.
 */
function leafCluster(ctx: CanvasRenderingContext2D, r: Rect, seed: number, base: [number, number, number]): void {
  const rand = rng(seed);
  const cx = r.x + r.w / 2;
  const cy = r.y + r.h / 2;
  const R = r.w * 0.37;
  ctx.save();
  ctx.beginPath();
  ctx.rect(r.x + 4, r.y + 4, r.w - 8, r.h - 8);
  ctx.clip();
  // Shadowed core: keeps the clump solid at a distance (mipmapped alpha).
  for (let i = 0; i < 20; i++) {
    const a = rand() * Math.PI * 2;
    const d = rand() * R * 0.5;
    ctx.fillStyle = rgb(base, 0.3 + rand() * 0.1);
    ctx.beginPath();
    ctx.arc(cx + Math.cos(a) * d, cy + Math.sin(a) * d * 0.85, R * (0.24 + rand() * 0.16), 0, Math.PI * 2);
    ctx.fill();
  }
  const light = (y: number): number => 0.62 + 0.5 * (1 - (y - (cy - R)) / (2 * R));
  // Inner leaves, mostly in shade.
  for (let i = 0; i < 300; i++) {
    const a = rand() * Math.PI * 2;
    const d = Math.sqrt(rand()) * R * 0.78;
    const x = cx + Math.cos(a) * d;
    const y = cy + Math.sin(a) * d;
    ctx.fillStyle = rgb(base, (light(y) - 0.2) * (0.75 + rand() * 0.3));
    leaf(ctx, x, y, rand() * Math.PI * 2, 13 + rand() * 7, 5 + rand() * 2);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }
  // Twigs radiating out, with leaves alternating along them: the crisp edge.
  const twigs = 18;
  for (let k = 0; k < twigs; k++) {
    const a = (k / twigs) * Math.PI * 2 + (rand() - 0.5) * 0.35;
    const reach = R * (0.72 + rand() * 0.3);
    const x0 = cx + Math.cos(a) * R * 0.2;
    const y0 = cy + Math.sin(a) * R * 0.2;
    const curl = (rand() - 0.5) * 0.5;
    ctx.strokeStyle = '#3a3022';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.quadraticCurveTo(cx + Math.cos(a + curl) * reach * 0.6, cy + Math.sin(a + curl) * reach * 0.6, cx + Math.cos(a) * reach, cy + Math.sin(a) * reach);
    ctx.stroke();
    let side = 1;
    for (let t = 0.3; t <= 1.0001; t += 0.085) {
      const ang = a + curl * (1 - t) * 0.8;
      const x = cx + Math.cos(ang) * reach * t;
      const y = cy + Math.sin(ang) * reach * t;
      const sheen = rand() < 0.15 ? 1.18 : 1;
      ctx.fillStyle = rgb(base, light(y) * (0.85 + rand() * 0.25) * sheen);
      const l = (15 + rand() * 7) * (1.05 - 0.3 * t);
      leaf(ctx, x, y, ang + side * (0.55 + rand() * 0.45), l, 4.5 + rand() * 2);
      // Leaf at the twig tip.
      if (t > 0.95) leaf(ctx, x, y, ang + (rand() - 0.5) * 0.3, l, 5);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      side = -side;
    }
  }
  ctx.restore();
}

/** Smooth pale tropical bark with vertical fissures and lichen patches. */
function bark(ctx: CanvasRenderingContext2D, r: Rect, seed: number): void {
  const rand = rng(seed);
  // Fill the whole quadrant so mip levels stay opaque at the inset UVs.
  const q = { x: r.x - 12, y: r.y - 12, w: r.w + 24, h: r.h + 24 };
  ctx.fillStyle = '#857a69';
  ctx.fillRect(q.x, q.y, q.w, q.h);
  for (let i = 0; i < 70; i++) {
    const x = q.x + rand() * q.w;
    ctx.strokeStyle = `rgba(45,36,26,${0.25 + rand() * 0.35})`;
    ctx.lineWidth = 1 + rand() * 2.5;
    ctx.beginPath();
    ctx.moveTo(x, q.y);
    ctx.bezierCurveTo(x + (rand() - 0.5) * 12, q.y + q.h * 0.3, x + (rand() - 0.5) * 12, q.y + q.h * 0.6, x + (rand() - 0.5) * 8, q.y + q.h);
    ctx.stroke();
  }
  for (let i = 0; i < 60; i++) {
    ctx.fillStyle = rand() < 0.5 ? `rgba(190,190,170,${0.15 + rand() * 0.2})` : `rgba(90,110,70,${0.15 + rand() * 0.2})`;
    ctx.beginPath();
    ctx.ellipse(q.x + rand() * q.w, q.y + rand() * q.h, 4 + rand() * 12, 3 + rand() * 8, 0, 0, Math.PI * 2);
    ctx.fill();
  }
}

/** Broadleaf atlas: three leaf-cluster sprites and a bark patch. */
export function broadleafAtlas(scene: Scene): DynamicTexture {
  const tex = new DynamicTexture('tex-broadleaf', { width: ATLAS_SIZE, height: ATLAS_SIZE }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, ATLAS_SIZE, ATLAS_SIZE);
  const hues: Array<[number, number, number]> = [
    [62, 112, 40],
    [86, 120, 42],
    [46, 98, 52],
  ];
  LEAF_SPRITES.forEach((r, i) => leafCluster(ctx, r, 40 + i, hues[i]!));
  bark(ctx, BARK_SPRITE, 47);
  tex.hasAlpha = true;
  tex.update(true);
  return tex;
}

/** One palm frond: a central rib with leaflets drooping off both sides. */
export function frondTexture(scene: Scene): DynamicTexture {
  const w = 128;
  const h = 512;
  const tex = new DynamicTexture('tex-frond', { width: w, height: h }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, w, h);
  const rand = rng(21);
  // Leaflets from base (bottom) to tip (top), shorter towards the tip.
  for (let y = h - 20; y > 10; y -= 5) {
    const t = 1 - y / h;
    const len = (w / 2 - 4) * Math.sin(Math.PI * Math.min(1, t * 1.15)) * (0.85 + rand() * 0.15);
    for (const side of [-1, 1]) {
      const g = 90 + Math.floor(rand() * 60);
      ctx.strokeStyle = `rgb(${40 + Math.floor(rand() * 30)},${g},${35 + Math.floor(rand() * 20)})`;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(w / 2, y);
      // Leaflets angle towards the tip and droop.
      ctx.quadraticCurveTo(w / 2 + side * len * 0.6, y - 10, w / 2 + side * len, y + 6);
      ctx.stroke();
    }
  }
  ctx.strokeStyle = '#6b6a3a';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(w / 2, h);
  ctx.lineTo(w / 2, 6);
  ctx.stroke();
  tex.update(true);
  return tex;
}

/** Bark with horizontal leaf-scar rings. */
export function barkTexture(scene: Scene): DynamicTexture {
  const tex = new DynamicTexture('tex-bark', { width: 64, height: 256 }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  const rand = rng(31);
  ctx.fillStyle = '#7a6a55';
  ctx.fillRect(0, 0, 64, 256);
  for (let y = 0; y < 256; y += 6 + rand() * 6) {
    ctx.fillStyle = `rgba(40,30,20,${0.25 + rand() * 0.25})`;
    ctx.fillRect(0, y, 64, 2);
  }
  tex.wrapU = WRAP;
  tex.wrapV = WRAP;
  tex.update(true);
  return tex;
}

/** An oil palm seen from the side: short trunk, big arching fronds (for crossed cards). */
export function oilPalmTexture(scene: Scene): DynamicTexture {
  const s = 256;
  const tex = new DynamicTexture('tex-oilpalm', { width: s, height: s }, scene, true);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, s, s);
  const rand = rng(51);
  const cx = s / 2;
  const top = s * 0.55;
  ctx.fillStyle = '#5b4b38';
  ctx.fillRect(cx - 9, top, 18, s - top);
  for (let i = 0; i < 18; i++) {
    // Fronds arch up and out, then droop at the tips.
    const a = Math.PI * (0.08 + 0.84 * (i / 17)) + (rand() - 0.5) * 0.1;
    const len = s * (0.36 + rand() * 0.12);
    const ex = cx - Math.cos(a) * len;
    const ey = top - Math.sin(a) * len * 0.55 + len * 0.35;
    const mx = cx - Math.cos(a) * len * 0.5;
    const my = top - Math.sin(a) * len * 0.75;
    const g = 85 + Math.floor(rand() * 50);
    ctx.strokeStyle = `rgb(${35 + Math.floor(rand() * 20)},${g},${30 + Math.floor(rand() * 15)})`;
    ctx.lineWidth = 7;
    ctx.beginPath();
    ctx.moveTo(cx, top);
    ctx.quadraticCurveTo(mx, my, ex, ey);
    ctx.stroke();
    // Leaflets along the frond.
    ctx.lineWidth = 2;
    for (let k = 0.2; k < 1; k += 0.08) {
      const x = (1 - k) * (1 - k) * cx + 2 * (1 - k) * k * mx + k * k * ex;
      const y = (1 - k) * (1 - k) * top + 2 * (1 - k) * k * my + k * k * ey;
      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + (rand() - 0.5) * 18, y + 6 + rand() * 8);
      ctx.stroke();
    }
  }
  tex.update(true);
  return tex;
}
